// voting.ts - Keep only the essential types
//
// The schemas are now authoritative — the TS types are derived via z.infer<>.
// Consumers can import { VoteType, VoteStats, VoteableEntity } for compile-time
// typing, or the matching *Schema exports for runtime validation.

import { z } from 'zod';

/**
 * Shared voting types for the Q4uDoc application
 */

/**
 * Represents a user's vote on a voteable entity
 */
export const VoteTypeSchema = z.enum(['up', 'down']).nullable();
export type VoteType = z.infer<typeof VoteTypeSchema>;

/**
 * Standard voting statistics for any voteable entity
 */
export const VoteStatsSchema = z.object({
  /** Number of upvotes */
  up: z.number(),
  /** Number of downvotes */
  down: z.number(),
  /** Calculated score (up - down) */
  score: z.number(),
  /** Current user's vote, if any */
  userVote: VoteTypeSchema.optional(),
});

export type VoteStats = z.infer<typeof VoteStatsSchema>;

/**
 * Type of entity that can be voted on
 */
export const VoteableEntitySchema = z.enum(['question', 'comment', 'answer']);
export type VoteableEntity = z.infer<typeof VoteableEntitySchema>;

/**
 * The body of every `POST /<entity>s/:id/vote`. `'none'` clears the vote — the wire cannot carry
 * the client's `null`, so the client maps it here and the server maps it back. One schema for
 * questions and comments so the generic client `voteOnItem` speaks to both.
 */
export const VoteRequestBodySchema = z.object({
  vote: z.enum(['up', 'down', 'none']),
});
export type VoteRequestBody = z.infer<typeof VoteRequestBodySchema>;
